export function fmtDurationMin(sec) {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  const m = Math.max(1, Math.round(Number(sec) / 60));
  return `${m} min`;
}

export function initials(name) {
  const parts = String(name || '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function isSageParticipant(person) {
  const name = String(person?.name || '').trim().toLowerCase();
  const role = String(person?.role || '').trim().toLowerCase();
  if (role === 'sage' || role === 'agent' || role === 'bot') return true;
  if (!name) return false;
  return (
    name === 'sage' ||
    name.startsWith('sage ') ||
    name.includes('sage ai') ||
    name === 'ai recruiter'
  );
}

export function meetingWhen(item) {
  const raw =
    item?.finalizedAt ||
    item?.scheduledStartTime ||
    item?.recordingStartedAt ||
    item?.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatCardDate(date) {
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function formatCardTime(date) {
  if (!date) return '';
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function groupByDay(items) {
  const groups = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  for (const item of items) {
    const when = meetingWhen(item);
    if (!when) continue;
    const day = new Date(when);
    day.setHours(0, 0, 0, 0);
    let label;
    if (day.getTime() === today.getTime()) label = 'Today';
    else if (day.getTime() === yesterday.getTime()) label = 'Yesterday';
    else {
      label = when.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(item);
  }
  return [...groups.entries()];
}

const AVATAR_TONES = ['violet', 'teal', 'amber', 'rose'];

/** Human participants only (Sage filtered out). */
export function participantAvatars(item) {
  const speak = (item?.speakingTime || []).filter((p) => !isSageParticipant(p));
  if (speak.length) {
    return speak.slice(0, 6).map((p, i) => {
      const name = p.name || `Participant ${i + 1}`;
      return {
        key: `${name}-${i}`,
        name,
        initials: initials(name),
        tone: AVATAR_TONES[i % AVATAR_TONES.length],
        role: p.role || 'participant',
      };
    });
  }
  if (item?.hostName) {
    return [
      {
        key: 'host',
        name: item.hostName,
        initials: initials(item.hostName),
        tone: 'violet',
        role: 'host',
      },
    ];
  }
  return [];
}

export function participantCount(item) {
  const humans = participantAvatars(item);
  if (humans.length) return humans.length;
  return item?.hostName ? 1 : 0;
}

/** Two slots for the card thumbnail (participant initials). */
export function thumbnailParticipants(item) {
  const people = participantAvatars(item);
  if (people.length >= 2) {
    return [people[0], people[1]];
  }
  if (people.length === 1) {
    return [
      people[0],
      {
        key: 'guest',
        name: 'Guest',
        initials: '?',
        tone: 'teal',
        role: 'guest',
      },
    ];
  }
  return [
    {
      key: 'a',
      name: item?.hostName || 'Host',
      initials: initials(item?.hostName || 'Host'),
      tone: 'violet',
      role: 'host',
    },
    {
      key: 'b',
      name: 'Guest',
      initials: '?',
      tone: 'teal',
      role: 'guest',
    },
  ];
}

/** Sage actually joined — do not infer from leftover S3 artifact URLs. */
export function hasSageInsights(item) {
  return item?.sagePresent === true;
}

export function hasUsableSummary(item) {
  if (!hasSageInsights(item)) return false;
  if (item?.summaryStatus === 'failed' || item?.summaryStatus === 'skipped') {
    return false;
  }
  return (item?.available_files || []).includes('summary');
}

export function hasUsableTranscript(item) {
  if (!hasSageInsights(item)) return false;
  return (item?.available_files || []).includes('transcript');
}

export function snippetForMeeting(item) {
  if (item?.summarySnippet) return String(item.summarySnippet);

  const files = item?.available_files || [];
  const sage = hasSageInsights(item);

  if (sage) {
    if (hasUsableSummary(item)) {
      return 'AI summary is ready — open for key topics, action items, and next steps.';
    }
    if (item?.summaryStatus === 'failed') {
      return 'Sage joined, but AI summary generation failed for this meeting.';
    }
    if (hasUsableTranscript(item)) {
      return 'Sage captured a transcript for this meeting. Open to review the conversation.';
    }
    return 'Sage joined this meeting. Open for AI insights when processing finishes.';
  }

  if (files.includes('recording')) {
    return 'Recording available. Sage was not in this meeting — no AI summary, transcript, or action items.';
  }
  return 'Sage was not invited. No AI summary, transcript, or action items for this meeting.';
}
